import React, { useEffect, useRef } from 'react';
import { useGameStore } from '../store/gameStore';
import { Mic, Radio, Zap, Info, X, ShieldCheck } from 'lucide-react';

interface ContextMenuProps {
  x: number;
  y: number;
  sectorId: string;
  onClose: () => void;
  triggerAnimation: (type: string) => void;
}

export const ContextMenu: React.FC<ContextMenuProps> = ({ x, y, sectorId, onClose, triggerAnimation }) => {
  const performAction = useGameStore(state => state.performAction);
  const selectSector = useGameStore(state => state.selectSector);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const handleAction = (action: 'Listen' | 'Broadcast' | 'Jam' | 'Secure') => {
    selectSector(sectorId);
    performAction(action);
    triggerAnimation(action);
    onClose();
  };

  return (
    <div 
      ref={menuRef}
      className="fixed z-[200] bg-gray-900 border border-blue-500 rounded-lg shadow-2xl p-2 w-48 animate-pop-in"
      style={{ top: y, left: x }}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div className="flex justify-between items-center mb-2 pb-2 border-b border-gray-700">
        <span className="text-xs font-bold text-blue-400 uppercase tracking-wider">Sector Actions</span>
        <button onClick={onClose} className="text-gray-500 hover:text-white">
          <X className="w-3 h-3" />
        </button>
      </div>
      
      <div className="flex flex-col gap-1">
        <button 
          onClick={() => handleAction('Listen')}
          className="flex items-center gap-2 px-2 py-1.5 hover:bg-blue-900/50 rounded text-sm text-gray-300 hover:text-white transition-colors text-left"
        >
          <Mic className="w-4 h-4 text-blue-400" />
          <span>Quick Listen</span>
        </button>
        <button 
          onClick={() => handleAction('Broadcast')}
          className="flex items-center gap-2 px-2 py-1.5 hover:bg-green-900/50 rounded text-sm text-gray-300 hover:text-white transition-colors text-left"
        >
          <Radio className="w-4 h-4 text-green-400" />
          <span>Quick Broadcast</span>
        </button>
        <button 
          onClick={() => handleAction('Jam')}
          className="flex items-center gap-2 px-2 py-1.5 hover:bg-red-900/50 rounded text-sm text-gray-300 hover:text-white transition-colors text-left"
        >
          <Zap className="w-4 h-4 text-red-400" />
          <span>Quick Jam</span>
        </button>
        <button 
          onClick={() => handleAction('Secure')}
          className="flex items-center gap-2 px-2 py-1.5 hover:bg-blue-900/50 rounded text-sm text-gray-300 hover:text-white transition-colors text-left"
        >
          <ShieldCheck className="w-4 h-4 text-blue-400" />
          <span>Secure Sector</span>
        </button>
        <div className="h-px bg-gray-700 my-1" />
        <button 
          onClick={() => { selectSector(sectorId); onClose(); }}
          className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-800 rounded text-sm text-gray-300 hover:text-white transition-colors text-left"
        >
          <Info className="w-4 h-4 text-gray-400" />
          <span>View Details</span>
        </button>
      </div>
    </div>
  );
};
